import {_, React, observer, observable, computed, IDisposer, action, useLocalStore, useEffect, useLayoutEffect, classNames, useAsObservableSource} from '@api'
import {AlignStyle, BINDING_DISTANCE, getFontFace, getFontSize, getFontStyle, getTextAlign, normalizeText, TextAreaUtils, TLDR, TldrawApp, useTldrawApp} from '@modules/whiteboard'
import {HTMLContainer, TLComponentProps} from '@tldraw/core'
import Vec from '@tldraw/vec'
import {stopPropagation} from '~components/stopPropagation'
import * as css from './TextShapeComponent.css'
import { TextUtil } from './TextUtil'
import {TextShape} from './'
import {ShapeComponentStore} from '../ShapeComponentStore'

/// region Store

export class TextShapeComponentStore extends ShapeComponentStore<TextShape, TextUtil> {
  constructor(public props: Omit<MyProps, 'className'> & { app: TldrawApp }) {
    super(props)
  }

  @computed get stroke() {
    return this.shape.style.textColor
  }

  registerEffects = () => {
    const _toDispose: Array<IDisposer> = []

    return () => {
      _toDispose.forEach(d => d())
    }
  }

  @observable.ref rInput: HTMLTextAreaElement
  @observable isMounted = false

  @action handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const {shape, bounds, props: {onShapeChange}, utils: {getBounds}} = this
    let delta = [0, 0]

    switch (shape.style.textAlign) {
      case 'start': {
        break
      }
      case 'middle': {
        const nextBounds = getBounds({
          ...shape,
          text: TLDR.normalizeText(e.currentTarget.value),
        })

        delta = Vec.div([nextBounds.width - bounds.width, 0], 2)
        break
      }
      case 'end': {
        const nextBounds = getBounds({
          ...shape,
          text: TLDR.normalizeText(e.currentTarget.value),
        })

        delta = [nextBounds.width - bounds.width, 0]
        break
      }
    }

    onShapeChange?.({
      ...shape,
      point: Vec.sub(shape.point, delta),
      text: TLDR.normalizeText(e.currentTarget.value),
    })
  }

  @action handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
   const {props: {shape, onShapeChange}} = this

    // If this keydown was just the meta key or a shortcut
    // that includes holding the meta key like (Command+V)
    // then leave the event untouched. We also have to explicitly
    // Implement undo/redo for some reason in order to get this working
    // in the vscode extension. Without the below code the following doesn't work
    //
    // - You can't cut/copy/paste when when text-editing/focused
    // - You can't undo/redo when when text-editing/focused
    // - You can't use Command+A to select all the text, when when text-editing/focused
    if (!(e.key === 'Meta' || e.metaKey)) {
      e.stopPropagation()
    } else if (e.key === 'z' && e.metaKey) {
      if (e.shiftKey) {
        document.execCommand('redo', false)
      } else {
        document.execCommand('undo', false)
      }
      e.stopPropagation()
      e.preventDefault()
      return
    }

    if (e.key === 'Escape' || (e.key === 'Enter' && (e.ctrlKey || e.metaKey))) {
      e.currentTarget.blur()
      return
    }

    if (e.key === 'Tab') {
      e.preventDefault()
      if (e.shiftKey) {
        TextAreaUtils.unindent(e.currentTarget)
      } else {
        TextAreaUtils.indent(e.currentTarget)
      }

      onShapeChange?.({...shape, text: normalizeText(e.currentTarget.value)})
    }
  }

  @action handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    e.currentTarget.setSelectionRange(0, 0)
    this.props.onShapeBlur?.()
  }

  @action handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    const {isMounted, props: {isEditing}} = this
    if (!isEditing) return
    if (!isMounted) return

    if (document.activeElement === e.currentTarget) {
      e.currentTarget.select()
    }
  }

  @action handlePointerDown = (e: React.PointerEvent) => {
    if (this.props.isEditing) {
      e.stopPropagation()

      if (!this.props.isSelected) {
        this.app.selection.select(this.shape.id)
      }
    }
  }

  @action setInputRef = r => this.rInput = r

  @computed get textDecoration() {
    const {shape: {style: {fontStyle}}} = this
    return classNames(fontStyle?.underline && 'underline', fontStyle?.strikethrough && 'line-through')
  }

  @computed get fontWeight() {
    const {shape: {style: {fontStyle}}} = this
    return fontStyle?.bold ? 'bold' : 'normal'
  }

  @computed get fontStyle() {
    const {shape: {style: {fontStyle}}} = this
    return fontStyle?.italic ? 'italic' : undefined
  }

  @computed get fontFace() {
    return getFontFace(this.shape.style.font)
  }
  @computed get cssStyle() {
    const {fontFace, shapeStyle, shapeStyle: {textColor, backgroundColor}, fontWeight, textDecoration, fontStyle} = this
    return {
      fontFamily: fontFace,
      color: textColor,
      backgroundColor,
      fontSize: getFontSize(shapeStyle),
      lineHeight: 1.3,
      textAlign: getTextAlign(shapeStyle.textAlign),
      fontWeight,
      textDecoration,
      fontStyle
    } as React.CSSProperties
  }
}

export const TextShapeComponentStoreContext = React.createContext<TextShapeComponentStore>(null)
export const useTextShapeComponentStore = () => React.useContext(TextShapeComponentStoreContext)

/// endregion

/// region Components

interface MyProps extends TLComponentProps<TextShape> {
  className?: string
}

export const TextShapeComponent = observer<MyProps, HTMLDivElement>(function TextShapeComponent({className, ...props}, ref) {
  const app = useTldrawApp(),
    obsProps = useAsObservableSource({...props, app}),
    store = useLocalStore(() => new TextShapeComponentStore(obsProps)),
    {
      setInputRef, cssStyle, props: {onShapeBlur, isEditing, isGhost, isBinding, events}, isResizing,
      app: {}, shape: {text, size}, shapeStyle: {textColor}, handleChange, handleKeyDown, handleBlur, handleFocus, handlePointerDown} = store

  useEffect(() => store.registerEffects(), [])

  React.useEffect(() => {
    if (isEditing) {
      requestAnimationFrame(() => {
        store.isMounted = true
        const elm = store.rInput
        if (elm) {
          elm.focus()
          elm.select()
        }
      })
    } else {
      onShapeBlur?.()
    }
  }, [isEditing])

  return (
    <HTMLContainer ref={ref} {...events}>
      <div className={css.wrapper} data-ghost={isGhost} data-editing={isEditing} data-resizing={isResizing} onPointerDown={handlePointerDown}>
        <div
          style={{...cssStyle,
            width: size ? `${size[0]}px` : undefined,
            height: size ? `${size[1]}px` : undefined
          }}
          className={css.innerWrapper}
        >
          {isBinding && (
            <div
              className='tl-binding-indicator'
              style={{
                position: 'absolute',
                top: -BINDING_DISTANCE,
                left: -BINDING_DISTANCE,
                width: `calc(100% + ${BINDING_DISTANCE * 2}px)`,
                height: `calc(100% + ${BINDING_DISTANCE * 2}px)`,
                backgroundColor: 'var(--tl-selectFill)'
              }}
            />
          )}
          {isEditing ? (
            <textarea
              className={css.textArea}
              ref={setInputRef}
              style={{
                ...cssStyle,
                textAlign: 'inherit'
              }}
              name='text'
              defaultValue={text}
              tabIndex={-1}
              autoComplete='false'
              autoCapitalize='false'
              autoCorrect='false'
              autoSave='false'
              autoFocus
              placeholder='Enter Text...'
              color={textColor}
              onFocus={handleFocus}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              onPointerDown={handlePointerDown}
              spellCheck='true'
              wrap='off'
              dir='auto'
              datatype='wysiwyg'
              onContextMenu={stopPropagation}
            />
          ) : (
             `${text}`
           )}
        </div>
        &#8203;
      </div>
    </HTMLContainer>
  )

  return (
    <TextShapeComponentStoreContext.Provider value={store}>
      <div className={classNames(css.root, className)}>

      </div>
    </TextShapeComponentStoreContext.Provider>)
}, {forwardRef: true})

/// endregion




// WEBPACK FOOTER //
// ./src/modules/whiteboard/shapes/text/TextShapeComponent.tsx